/**
 * Entity Selection Store Factory
 *
 * Creates standardized single-entity selection stores for modules.
 * This factory eliminates boilerplate for stores that only track which
 * single entity (by ID) is currently selected/viewed.
 *
 * @example
 * ```ts
 * export const useLeadUIStore = createEntitySelectionStore('lead');
 *
 * // Usage in components:
 * const selectedId = useLeadUIStore.use.selectedLeadId();
 * const setSelected = useLeadUIStore.use.setSelectedLead();
 *
 * // With object:
 * setSelected(leadObject); // Sets selectedLeadId to leadObject.id
 *
 * // With ID directly:
 * const selectById = useLeadUIStore.use.selectLeadById();
 * selectById('lead-123');
 * ```
 */

import { create } from 'zustand';
import { createSelectors } from './createSelectors';

/**
 * Configuration for entity selection store
 */
export interface EntitySelectionConfig {
  /** The entity name (e.g., 'lead', 'deal', 'entity') - used to generate method names */
  entityName: string;
}

/**
 * Base state interface for entity selection stores
 */
interface BaseEntitySelectionState {
  [key: string]: any;
}

/**
 * Creates a zustand store for tracking single entity selection by ID
 *
 * @param entityName - Name of the entity (e.g., 'lead', 'deal')
 * @returns A zustand store with selectors for entity selection
 *
 * Generated state shape:
 * - `selected{Entity}Id`: string | null - The selected entity ID
 * - `setSelected{Entity}`: (entity: { id: string } | null) => void - Set selection from object
 * - `select{Entity}ById`: (id: string) => void - Set selection by ID directly
 */
export function createEntitySelectionStore(entityName: string) {
  // Capitalize first letter for method names
  const capitalizedName = entityName.charAt(0).toUpperCase() + entityName.slice(1);

  // Generate property/method names
  const selectedIdKey = `selected${capitalizedName}Id`;
  const setSelectedKey = `setSelected${capitalizedName}`;
  const selectByIdKey = `select${capitalizedName}ById`;

  // Create the store with properly typed state
  const store = create<BaseEntitySelectionState>()((set) => ({
    [selectedIdKey]: null as string | null,

    [setSelectedKey]: (entity: { id: string } | null) => {
      set({ [selectedIdKey]: entity?.id || null } as any);
    },

    [selectByIdKey]: (id: string) => {
      set({ [selectedIdKey]: id } as any);
    },
  }));

  // Add selectors for easier usage
  return createSelectors(store) as any;
}

/**
 * Type-safe wrapper for entity selection stores
 * Use this for better TypeScript support
 */
export type EntitySelectionStore<EntityName extends string> = ReturnType<typeof createEntitySelectionStore> & {
  getState: () => {
    [K in `selected${Capitalize<EntityName>}Id`]: string | null;
  } & {
    [K in `setSelected${Capitalize<EntityName>}`]: (entity: { id: string } | null) => void;
  } & {
    [K in `select${Capitalize<EntityName>}ById`]: (id: string) => void;
  };
};
